// Helpers for the appConfig/main `bankLoanTypeLocations` map.
//
// The document stores the data in REVERSE order relative to how the UI walks it:
//
//   bankLoanTypeLocations = {
//     [lender]: {
//       [loanType]: string[]   // <- locations
//     }
//   }
//
// e.g. { "Aditya Birla Finance": { "Home Loan": ["Dhanbad", "Aurangabad"], ... } }
//
// The three-layer selector (Banks & Products, Payouts & DSA) walks it the other
// way: Location → Loan Type → Lender. These helpers invert the map so callers
// can ask "which loan types exist in this city" and "which lenders offer this
// loan type in this city" without re-deriving the inversion each time.

export type BankLoanTypeLocations = Record<string, Record<string, string[]>>;

export interface LocationCard {
  key: string;
  label: string;
  emoji: string;
}

const norm = (s: string) => s.trim().toLowerCase();

/**
 * appConfig/main also carries the canonical, admin-curated list of every
 * location (the full city list — Jabalpur, Ujjain, Sagar, etc.), each shaped
 * like { key, label, emoji }. That list is NOT the same as the locations
 * referenced inside `bankLoanTypeLocations` (which only contains the subset
 * of cities a lender has actually been mapped to so far).
 *
 * The exact top-level field name for this array isn't fixed by any code in
 * this repo (it was authored directly in the Firestore console), so instead
 * of hardcoding a field name, this scans every top-level field on the raw
 * appConfig/main document and picks out whichever one is an array of objects
 * shaped like { key: string, label: string }. Any matching arrays found are
 * merged and de-duplicated by key.
 */
export function extractLocationCards(mainDocData: Record<string, unknown> | undefined): LocationCard[] {
  if (!mainDocData) return [];
  const byKey = new Map<string, LocationCard>();

  for (const value of Object.values(mainDocData)) {
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (
        item && typeof item === 'object' &&
        typeof (item as Record<string, unknown>).key === 'string' &&
        typeof (item as Record<string, unknown>).label === 'string'
      ) {
        const key = (item as Record<string, unknown>).key as string;
        const label = (item as Record<string, unknown>).label as string;
        const emoji = typeof (item as Record<string, unknown>).emoji === 'string'
          ? (item as Record<string, unknown>).emoji as string
          : '📍';
        const normalizedKey = norm(key);
        if (normalizedKey && !byKey.has(normalizedKey)) {
          byKey.set(normalizedKey, { key: key.trim(), label: label.trim(), emoji });
        }
      }
    }
  }

  return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/** All distinct locations across every lender/loan-type, sorted A→Z. */
export function getAllLocations(map: BankLoanTypeLocations): string[] {
  const set = new Set<string>();
  for (const loanTypes of Object.values(map || {})) {
    for (const locations of Object.values(loanTypes || {})) {
      for (const loc of locations || []) {
        if (loc?.trim()) set.add(loc.trim());
      }
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** Loan types that have at least one lender offering them in `location`, sorted A→Z. */
export function getLoanTypesForLocation(
  map: BankLoanTypeLocations,
  location: string,
): string[] {
  if (!location) return [];
  const set = new Set<string>();
  for (const loanTypes of Object.values(map || {})) {
    for (const [loanType, locations] of Object.entries(loanTypes || {})) {
      if ((locations || []).some((l) => norm(l) === norm(location))) {
        set.add(loanType);
      }
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** Lenders that offer `loanType` in `location`, sorted A→Z. */
export function getLendersForLocationLoanType(
  map: BankLoanTypeLocations,
  location: string,
  loanType: string,
): string[] {
  if (!location || !loanType) return [];
  const set = new Set<string>();
  for (const [lender, loanTypes] of Object.entries(map || {})) {
    const locations = loanTypes?.[loanType];
    if (locations && locations.some((l) => norm(l) === norm(location))) {
      set.add(lender);
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}